import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, LayoutAnimation } from 'react-native';
import { LinkedAccount, Invitation } from '@/types/residents/home';
import { UserType } from '@/types/residents/user';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchInvitations, acceptInvitation, resendInvitation } from '@/store/slices/residents/invitationsSlice';
import { useToast } from '@/contexts/admin/ToastContext';
import { getDisplayName } from '@/utils/residents/utils';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { PageHeader, SectionHeader } from '@/components/residents/redesign';
import {
  LinkedAccountsTabSegment,
  LinkedAccountCard,
  InvitationCard,
  InviteCTA,
} from '@/components/residents/redesign/linkedAccounts';
import type { LinkedAccountsTab } from '@/components/residents/redesign/linkedAccounts';
import { MobileEmptyState } from '@/components/residents/linkedAccounts/mobile/MobileEmptyState';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { LinkedAccountsListSkeleton, InvitationsListSkeleton } from './DesktopLinkedAccountsViewSkeleton';

interface DesktopLinkedAccountsViewProps {
  linkedAccounts: LinkedAccount[];
  isLoading: boolean;
  onRefresh: () => void;
  onRemove: (account: LinkedAccount) => void;
  onSendInvite: () => void;
  onDeclineInvitation?: (invitation: Invitation) => void;
  onCancelInvitation?: (invitation: Invitation) => void;
}

export const DesktopLinkedAccountsView: React.FC<DesktopLinkedAccountsViewProps> = ({
  linkedAccounts,
  isLoading,
  onRefresh,
  onRemove,
  onSendInvite,
  onDeclineInvitation,
  onCancelInvitation,
}) => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const { invitations, isLoading: isLoadingInvites } = useAppSelector((s) => s.invitations);
  const { user } = useAppSelector((s) => s.auth);
  const { isTablet } = useResponsive();
  const { showSuccess, showError } = useToast();
  const isResident = user?.type === UserType.RESIDENT;

  const [activeTab, setActiveTab] = useState<LinkedAccountsTab>('accounts');

  useEffect(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  const handleTabChange = useCallback((tab: LinkedAccountsTab) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setActiveTab(tab);
  }, []);

  const handleAccept = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(acceptInvitation(invitation.id)).unwrap();
      const displayName = getDisplayName(invitation.user.firstName, invitation.user.lastName) || invitation.user.email;
      showSuccess(`You have successfully accepted an invitation from ${displayName}`);
      onRefresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to accept invitation');
    }
  }, [dispatch, showSuccess, showError, onRefresh]);

  const handleResend = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(resendInvitation(invitation.id)).unwrap();
      showSuccess(`Invitation resent to ${invitation.user.email}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to resend invitation');
    }
  }, [dispatch, showSuccess, showError]);

  const handleRefreshAll = useCallback(() => {
    onRefresh();
    dispatch(fetchInvitations());
  }, [onRefresh, dispatch]);

  const receivedInvitations = useMemo(
    () => invitations.filter((inv) => !inv.sender),
    [invitations],
  );

  const useWideLayout = !isTablet;

  const renderAccountsList = () => {
    if (isLoading) {
      return <LinkedAccountsListSkeleton />;
    }

    if (linkedAccounts.length === 0) {
      return (
        <MobileEmptyState
          icon={<RESIDENTS_SVG_COMPONENTS.NO_CONNECTION width={60} height={60} color={colors.text.dimmer} />}
          title="No connected accounts"
          description="You don't have any linked accounts yet."
          iconWidth={60}
          iconHeight={60}
        />
      );
    }

    return (
      <View style={styles.cardList}>
        {linkedAccounts.map((account) => (
          <LinkedAccountCard
            key={account.id}
            account={account}
            onRemove={onRemove}
          />
        ))}
      </View>
    );
  };

  const renderInvitationsList = () => {
    if (isLoadingInvites) {
      return <InvitationsListSkeleton />;
    }

    if (invitations.length === 0) {
      return (
        <MobileEmptyState
          icon={<RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX width={60} height={60} color={colors.text.muted} />}
          title="No invitations"
          description="You don't have any pending invitations."
          iconWidth={60}
          iconHeight={60}
        />
      );
    }

    return (
      <View style={styles.cardList}>
        {invitations.map((invitation) => (
          <InvitationCard
            key={invitation.id}
            invitation={invitation}
            onAccept={!invitation.sender ? handleAccept : undefined}
            onDecline={!invitation.sender ? onDeclineInvitation : undefined}
            onCancel={invitation.sender ? onCancelInvitation : undefined}
            onResend={invitation.sender ? handleResend : undefined}
          />
        ))}
      </View>
    );
  };

  // Wide layout: two columns (accounts left, invitations + CTA right)
  if (useWideLayout) {
    return (
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isLoading || isLoadingInvites}
            onRefresh={handleRefreshAll}
            tintColor={colors.text.primary}
          />
        }
      >
        <PageHeader
          title="Linked Accounts"
          subtitle="Manage connections & invitations"
        />

        <LinkedAccountsTabSegment
          selectedTab={activeTab}
          accountsCount={linkedAccounts.length}
          invitationsCount={invitations.length}
          onTabChange={handleTabChange}
        />

        <View style={styles.twoColumnRow}>
          {/* Left Column: Active tab content */}
          <View style={styles.columnFlex}>
            {activeTab === 'accounts' ? (
              <>
                <SectionHeader title="Connected" />
                {renderAccountsList()}
              </>
            ) : (
              <>
                <SectionHeader title="All Invitations" />
                {renderInvitationsList()}
              </>
            )}
          </View>

          {/* Right Column: Pending preview + Invite CTA */}
          <View style={styles.columnFlex}>
            {activeTab === 'accounts' && receivedInvitations.length > 0 ? (
              <>
                <SectionHeader
                  title="Pending"
                  actionLabel="View all"
                  onAction={() => handleTabChange('invitations')}
                />
                <View style={styles.cardList}>
                  {receivedInvitations.slice(0, 3).map((invitation) => (
                    <InvitationCard
                      key={invitation.id}
                      invitation={invitation}
                      onAccept={handleAccept}
                      onDecline={onDeclineInvitation}
                    />
                  ))}
                </View>
                {isResident && (
                  <View style={styles.ctaWrapper}>
                    <InviteCTA onSendInvite={onSendInvite} minHeight={INVITE_CTA_MIN_HEIGHT} />
                  </View>
                )}
              </>
            ) : (
              isResident && (
                <>
                  <SectionHeader title="Invite" />
                  <InviteCTA onSendInvite={onSendInvite} minHeight={INVITE_CTA_MIN_HEIGHT} />
                </>
              )
            )}
          </View>
        </View>
      </ScrollView>
    );
  }

  // Narrow/tablet layout: single column with tabs
  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isLoading || isLoadingInvites}
          onRefresh={handleRefreshAll}
          tintColor={colors.text.primary}
        />
      }
    >
      <PageHeader
        title="Linked Accounts"
        subtitle="Manage connections & invitations"
      />

      <LinkedAccountsTabSegment
        selectedTab={activeTab}
        accountsCount={linkedAccounts.length}
        invitationsCount={invitations.length}
        onTabChange={handleTabChange}
      />

      {activeTab === 'accounts' ? (
        <>
          <SectionHeader title="Connected" />
          {renderAccountsList()}

          {receivedInvitations.length > 0 && (
            <>
              <SectionHeader
                title="Pending"
                actionLabel="View all"
                onAction={() => handleTabChange('invitations')}
              />
              <View style={styles.cardList}>
                {receivedInvitations.slice(0, 2).map((invitation) => (
                  <InvitationCard
                    key={invitation.id}
                    invitation={invitation}
                    onAccept={handleAccept}
                    onDecline={onDeclineInvitation}
                  />
                ))}
              </View>
            </>
          )}

          {isResident && (
            <View style={styles.ctaWrapper}>
              <InviteCTA onSendInvite={onSendInvite} />
            </View>
          )}
        </>
      ) : (
        <>
          {isResident && (
            <View style={styles.ctaWrapperTop}>
              <InviteCTA onSendInvite={onSendInvite} />
            </View>
          )}
          <SectionHeader title="All Invitations" />
          {renderInvitationsList()}
        </>
      )}
    </ScrollView>
  );
};

export default DesktopLinkedAccountsView;

const HP = REDESIGN_SPACING.horizontalPadding;

// Matches the LinkedAccountCard height with 3 detail rows so the Invite CTA
// aligns with the Connected card on the Accounts tab and stays consistent on
// the Invitations tab.
const INVITE_CTA_MIN_HEIGHT = 250;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    padding: HP,
    paddingBottom: 60,
  },
  twoColumnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  columnFlex: {
    flex: 1,
  },
  cardList: {
    gap: 12,
  },
  ctaWrapper: {
    marginTop: 24,
  },
  ctaWrapperTop: {
    marginTop: 20,
  },
});
